import type { ReactNode } from 'react'

/**
 * The classroom app shell (design/style-c, "App shell"): the page itself
 * never scrolls. A sidebar on the left, then a column on the pale-blue field
 * with a fixed top bar, a rounded paper panel that scrolls, and a fixed
 * bottom bar.
 *
 * At ≤ 820px the sidebar becomes a bottom tab bar (the sidebar comes first
 * in the DOM, so the frame stacks in reverse on phones), the top bar hides,
 * and the panel loses its frame: the whole column scrolls, bottom bar
 * included, so a page's action is still at the end of its content.
 */
export function AppFrame({ children, studio = false }: { children: ReactNode; studio?: boolean }) {
  return (
    <div
      className={[
        'cl flex h-dvh flex-col-reverse overflow-hidden desk:flex-row',
        studio ? 'leading-normal' : '',
      ].join(' ')}
    >
      {children}
    </div>
  )
}

type Density = 'student' | 'studio'

/**
 * One screen: top bar, scrolling panel, bottom bar.
 * `top` is the screen's title plus at most one control; `bottom` one primary
 * action plus one quiet link — the calm rules.
 */
export function Column({
  top,
  bottom,
  children,
  density = 'student',
  topOnPhone = false,
  panelClassName = '',
}: {
  top?: ReactNode
  bottom?: ReactNode
  children: ReactNode
  density?: Density
  /** Keep the top bar on phones — for a screen whose only control lives there. */
  topOnPhone?: boolean
  panelClassName?: string
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-class-paper desk:overflow-hidden desk:bg-class-field">
      {top && (
        <div
          className={[
            'shrink-0 items-center gap-4 desk:flex',
            density === 'studio' ? 'h-16 px-5 desk:px-7' : 'h-[68px] px-5 desk:px-8',
            topOnPhone ? 'flex' : 'hidden',
          ].join(' ')}
        >
          {top}
        </div>
      )}
      <div
        className={[
          'cl-scroll min-h-0 flex-none bg-class-paper desk:mx-4 desk:flex-1 desk:overflow-y-auto desk:rounded-class-lg desk:shadow-[inset_0_0_0_1px_var(--color-class-hairline)]',
          !bottom ? 'desk:mb-4' : '',
          panelClassName,
        ].join(' ')}
      >
        {children}
      </div>
      {bottom && (
        <div
          className={[
            'flex shrink-0 flex-wrap items-center gap-4 py-2.5 desk:min-h-[72px] desk:flex-nowrap',
            density === 'studio' ? 'px-5 desk:px-7' : 'px-5 desk:px-8',
          ].join(' ')}
        >
          {bottom}
        </div>
      )}
    </div>
  )
}

/** The content column inside the panel. */
export function Main({
  children,
  density = 'student',
  width,
  className = '',
}: {
  children: ReactNode
  density?: Density
  /** Override the max width in px (reading column 680–720, lists 820, Studio 1040). */
  width?: number
  className?: string
}) {
  const max = width ?? (density === 'studio' ? 1040 : 820)
  return (
    <main
      style={{ maxWidth: max }}
      className={[
        'mx-auto flex flex-col px-5 pt-8 pb-8 desk:px-12',
        density === 'studio' ? 'gap-10 desk:gap-12 desk:pt-10 desk:pb-14' : 'gap-10 desk:gap-14 desk:pt-12 desk:pb-16',
        className,
      ].join(' ')}
    >
      {children}
    </main>
  )
}

/** The screen title in the top bar — 15px, 600 (student) or 700 (Studio). */
export function TopTitle({ children, studio = false }: { children: ReactNode; studio?: boolean }) {
  return <p className={['m-0 truncate', studio ? 'font-bold' : 'font-semibold'].join(' ')}>{children}</p>
}
